import {
  Box,
  MenuItem,
  useTheme,
} from '@mui/material';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { isValidPhoneNumber } from '../../../common/utils/formUtils';
import {
  ErrorAlert, IFormInputs, StyledSelect, styledSelectMenuProps, StyledTextField,
} from '../../../common/StyledFormInputs';
import { TestIds } from '../../../assets/testIds';
import { FormFieldBox } from '../../../common/FormFieldBox';
import { CompanyRoleOptions } from '../../../common/models/Question';
import { Member } from '../../../common/models/Member';

interface SettingsMemberFormProps {
  memberInformation?: Member;
  companyRoleOptions?: CompanyRoleOptions[];
}

export function AccountSettingsMemberFormControl({
  companyRoleOptions,
}: SettingsMemberFormProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  const { register, control, formState: { errors } } = useFormContext<IFormInputs>();

  return (
    <Box
      sx={{
        maxWidth: theme.spacing(64),
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(2),
        width: theme.spacing(64),
        [theme.breakpoints.down('sm')]: {
          width: '100%',
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          gap: theme.spacing(2),
          alignSelf: 'stretch',
          [theme.breakpoints.down('sm')]: {
            flexDirection: 'column',
          },
        }}
      >
        {/* First Name Field */}
        <FormFieldBox fieldName={t('onboarding.questionPlaceholder.First Name')} isRequired={true}>
          <>
            <StyledTextField
              sx={{ marginBottom: theme.spacing(0) }}
              data-testid={TestIds.accountSettingsPage.firstNameTxt}
              placeholder='First Name'
              error={!!errors.memberInfo?.firstName}
              {...register('memberInfo.firstName', { required: true })}
            />
            {errors.memberInfo?.firstName && <ErrorAlert />}
          </>
        </FormFieldBox>
        {/* Last Name Field */}
        <FormFieldBox fieldName={t('onboarding.questionPlaceholder.Last Name')} isRequired={true}>
          <>
            <StyledTextField
              sx={{ marginBottom: theme.spacing(0) }}
              data-testid={TestIds.accountSettingsPage.lastNameTxt}
              placeholder='Last Name'
              error={!!errors.memberInfo?.lastName}
              {...register('memberInfo.lastName', { required: true })}
            />
            {errors.memberInfo?.lastName && <ErrorAlert />}
          </>
        </FormFieldBox>
      </Box>
      {/* Email Field */}
      <FormFieldBox fieldName={t('onboarding.questionPlaceholder.Email')} isRequired={true}>
        <>
          <StyledTextField
            sx={{ marginBottom: theme.spacing(0) }}
            disabled
            data-testid={TestIds.accountSettingsPage.emailTxt}
            placeholder='Email'
            error={!!errors.memberInfo?.email}
            {...register('memberInfo.email', {
              required: true,
              setValueAs: (value) => value.trim(),
            })}
          />
          {errors.memberInfo?.email && <ErrorAlert />}
        </>
      </FormFieldBox>
      {/* Role Field */}
      <FormFieldBox fieldName={t('onboarding.account.roleAtCompany')} isRequired={true}>
        <>
          <Controller
            name='businessInfo.role'
            control={control}
            rules={{ required: true }}
            render={({ field, fieldState }) => (
              <StyledSelect
                {...field}
                displayEmpty
                value={field.value ?? ''}
                data-testid={TestIds.accountSettingsPage.roleAtCompanyTxt}
                error={!!fieldState.error}
                MenuProps={styledSelectMenuProps}
              >
                {!field.value && (
                  <MenuItem disabled value=''>
                    {t('onboarding.questionPlaceholder.selectRole')}
                  </MenuItem>
                )}
                {companyRoleOptions?.map((option) => (
                  <MenuItem key={option.id} value={option.id}>
                    {option.name}
                  </MenuItem>
                ))}
              </StyledSelect>
            )}
          />
          {errors.businessInfo?.role && <ErrorAlert />}
        </>
      </FormFieldBox>
      {/* Phone Field */}
      <FormFieldBox fieldName={t('onboarding.questionPlaceholder.phone')} isRequired={false}>
        <>
          <StyledTextField
            sx={{
              marginBottom: errors.memberInfo?.phone ?
                theme.spacing(0) :
                theme.spacing(2),
            }}
            data-testid={TestIds.accountSettingsPage.phoneTxt}
            placeholder='Phone'
            error={!!errors.memberInfo?.phone}
            {...register('memberInfo.phone', {
              required: false,
              validate: (value) => (!value || isValidPhoneNumber(value)) || `${t('error.invalidPhoneNumber')}`,
            })}
          />
          {errors.memberInfo?.phone &&
          <ErrorAlert
            optionalStyles={{ marginBottom: theme.spacing(2) }}
            errorMessage={errors.memberInfo?.phone?.message}
          />}
        </>
      </FormFieldBox>
    </Box>
  );
}
